import React, { useCallback, useEffect, useMemo } from 'react';
import parse from 'html-react-parser';
import { ChildNode, Element } from 'domhandler';
import { List } from 'immutable';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    AttachmentKey,
    ConversationMessageKey,
    TicketAttachment,
} from 'flow/types/TicketEventData';
import { TicketKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import {
    selectEventDirection,
    selectEventMessageText,
    selectTicketMessageAttachments,
} from 'flow/selectors/TicketEventSelectors';
import { EmailTicketMessageAttachment } from '../Messages/TicketMessageAttachment';
import { getNodeText } from './LinkElementConverter';
import {
    convertPlainTextNewlines,
    getSanitizedHtml,
} from './email-sanitize-utils';
import useAppContext from 'flow/hooks/useAppContext';
import { useModal } from 'flow/contexts/ModalProvider';

interface Props {
    ticketKey: TicketKey;
    eventKey: ConversationMessageKey;
    sentTime?: string;
    onAttachmentsInlined: (attachmentsInlined: Set<AttachmentKey>) => void;
}

const EmailBody = ({
    ticketKey,
    eventKey,
    sentTime,
    onAttachmentsInlined,
}: Props) => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();
    const message = useAppSelector((state) =>
        selectEventMessageText(state, ticketKey, eventKey),
    );

    const attachments = useAppSelector((state) =>
        selectTicketMessageAttachments(state, ticketKey, eventKey),
    );

    const direction = useAppSelector((state) =>
        selectEventDirection(state, ticketKey, eventKey),
    );

    // Remote (hotlinked) images are only auto-loaded for OUTGOING messages,
    // whose HTML is agent-authored (composed reply + signature). INCOMING
    // bodies are sender-controlled: rendering <img src> would fire a GET to an
    // arbitrary URL the moment the email is opened (tracking pixels, CSRF via
    // GET). NOTE: this trust rests on outbound event bodies staying
    // agent-authored — if stored outbound bodies ever start embedding quoted
    // thread history, this gate must be revisited.
    const trustRemoteImages = direction === 'OUTGOING';

    const contentSanitized = useMemo(
        () => getSanitizedHtml(convertPlainTextNewlines(message)), // THIS IS IMPORTANT. UNDER NO CIRCUMSTANCES do we want to render unsanitized HTML
        [message],
    );

    // Links in email content are never followed directly: clicking one opens a
    // confirmation dialog showing the real destination, so the recipient can
    // verify where it leads before we open it in a new tab.
    const handleLinkClick = useCallback(
        (url: string) => {
            openModal('confirmationDialog', {
                title: i18next.t('app.confirm'),
                description: (
                    <>
                        {i18next.t('tickets.message.link.confirm_body')}
                        <span className="text-link mt-1 block font-medium break-all">
                            {url}
                        </span>
                        <span className="mt-2 block">
                            {i18next.t('tickets.message.link.confirm_question')}
                        </span>
                    </>
                ),
                confirmButtonText: i18next.t('tickets.message.link.open'),
                handleAccept: () =>
                    window.open(url, '_blank', 'noopener,noreferrer'),
            });
        },
        [openModal, i18next],
    );

    const { parsedContent, inlinedKeys } = useMemo(
        () =>
            renderEmailHtml(contentSanitized, {
                attachments,
                sentTime,
                i18next,
                onLinkClick: handleLinkClick,
                trustRemoteImages,
            }),
        [
            contentSanitized,
            attachments,
            sentTime,
            i18next,
            handleLinkClick,
            trustRemoteImages,
        ],
    );

    useEffect(() => {
        onAttachmentsInlined(inlinedKeys);
    }, [inlinedKeys, onAttachmentsInlined]);

    return (
        // Tailwind's preflight strips default browser styles (margins, list-style, etc.) from HTML elements.
        // Since we render sanitized email HTML, we restore essential spacing and styling for block-level elements.
        // whitespace-normal overrides the inherited whitespace-pre-wrap, which otherwise renders
        // whitespace between HTML tags as visible lines (e.g. nested empty divs each adding 24px of height).
        <div
            className={[
                'whitespace-normal',
                '[&_p]:my-6',
                '[&_h1]:my-6 [&_h2]:my-5 [&_h3]:my-4',
                '[&_ul]:my-4 [&_ul]:list-disc [&_ul]:ps-6',
                '[&_ol]:my-4 [&_ol]:list-decimal [&_ol]:ps-6',
                '[&_blockquote]:my-4 [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:ps-4',
            ].join(' ')}
        >
            {parsedContent}
        </div>
    );
};

export default React.memo(EmailBody);

type RenderEmailHtmlOptions = {
    attachments?: List<TypedMap<TicketAttachment>>;
    sentTime?: string;
    i18next: { t: (key: string) => string };
    onLinkClick: (url: string) => void;
    trustRemoteImages?: boolean;
};

// Parses sanitized email HTML into React nodes, replacing <img>/<a> elements
// with our own components. Kept at module scope (not inside EmailBody) so the
// per-element render helpers aren't redefined on every render. Returns the
// parsed content and the set of attachment keys that were rendered inline.
const renderEmailHtml = (
    contentSanitized: string,
    {
        attachments,
        sentTime,
        i18next,
        onLinkClick,
        trustRemoteImages,
    }: RenderEmailHtmlOptions,
) => {
    const inlinedKeys = new Set<AttachmentKey>();

    // Intercept link clicks so they go through the confirmation dialog instead
    // of navigating directly.
    const onConfirmLinkClick = (href: string) => (event: React.MouseEvent) => {
        event.preventDefault();
        onLinkClick(href);
    };

    const renderImgNode = (imgNode: Element, interactive = true) => {
        const attachmentToDisplayInline = getAttachmentMatchingImgTag(
            imgNode,
            attachments,
        );

        if (attachmentToDisplayInline) {
            inlinedKeys.add(attachmentToDisplayInline.get('key'));
            const imgWidth = parseImgDimension(imgNode.attribs?.width);
            const imgHeight = parseImgDimension(imgNode.attribs?.height);
            // Matching attachment found - replace <img> with EmailTicketMessageAttachment component
            return (
                <EmailTicketMessageAttachment
                    key={`ebma-${attachmentToDisplayInline.get('key')}`}
                    attachment={attachmentToDisplayInline}
                    sentTime={sentTime}
                    htmlWidth={imgWidth}
                    htmlHeight={imgHeight}
                    interactive={interactive}
                />
            );
        }

        // No matching attachment \u2014 handle by URI scheme:
        // \u2022 data:image/ (excluding SVG): render inline (no network request, SVG excluded for script safety)
        // \u2022 https on OUTGOING (agent-authored) messages: render directly (signatures use external image URLs)
        // \u2022 https on INCOMING messages: TO BE CAUTIOUS, we do NOT render the <img> with its src \u2014 that
        //   would fire a GET to a sender-controlled URL on open. Shown as a confirm-on-click link instead.
        // \u2022 Other protocols (http, ftp, etc.): broken image with alt text for troubleshooting

        const srcUrl = imgNode.attribs?.src;

        if (DATA_IMAGE_ALLOWED.test(srcUrl ?? '')) {
            const imgWidth = parseImgDimension(imgNode.attribs?.width);
            const imgHeight = parseImgDimension(imgNode.attribs?.height);
            return (
                <span>
                    <img
                        src={srcUrl}
                        alt={imgNode.attribs?.alt || ''}
                        width={imgWidth}
                        height={imgHeight}
                    />
                    <span className="block text-xs text-gray-500 italic">
                        {i18next.t('tickets.message.image.embedded_info')}
                    </span>
                </span>
            );
        }

        if (srcUrl?.startsWith(URI.HTTPS)) {
            if (trustRemoteImages) {
                const imgWidth = parseImgDimension(imgNode.attribs?.width);
                const imgHeight = parseImgDimension(imgNode.attribs?.height);
                return (
                    <img
                        src={srcUrl}
                        alt={imgNode.attribs?.alt || ''}
                        width={imgWidth}
                        height={imgHeight}
                        style={{ maxWidth: '100%' }}
                    />
                );
            }
            return (
                <a
                    href={srcUrl}
                    onClick={onConfirmLinkClick(srcUrl)}
                    className="text-link cursor-pointer"
                >
                    {srcUrl}
                </a>
            );
        }

        // For other protocols (http (non-secure), ftp, etc.), we just render the src URL with a small broken image icon. This for troubleshooting purposes.
        return <img className="italic" alt={srcUrl} />;
    };

    const renderLinkNode = (linkNode: Element) => {
        // <a> tags: special handling to only render https:// mailto: and tel: links as actual links
        const href = linkNode.attribs?.href;
        if (!href) {
            // No href (empty, missing, or stripped by DOMPurify for dangerous schemes like javascript:) \u2192 show link text only
            return <span>{getNodeText(linkNode)}</span>;
        }
        if (!URI_A_TAG_ALLOWED_LIST.test(href)) {
            // href is not an allowed scheme (https://, mailto:, tel:) \u2192 show the URI as plain italic text so it's visible but not clickable
            const linkText = getNodeText(linkNode)?.trim();
            const textIsRedundant = !linkText || href.includes(linkText);
            return (
                <span className="italic">
                    {!textIsRedundant && `${linkText}: `}
                    {href}
                </span>
            );
        }
        // href is an allowed scheme \u2192 render its own text (or the URL if it has
        // none) as a link; clicking confirms the destination before opening, so
        // we no longer need to spell the raw URL out beside mismatched text.
        const linkText = getNodeText(linkNode)?.trim();
        return (
            <a
                href={href}
                onClick={onConfirmLinkClick(href)}
                className="text-link cursor-pointer"
            >
                {linkText || href}
            </a>
        );
    };

    const parsedContent = parse(contentSanitized, {
        replace: (domNode: Element) => {
            if (isEmptyParagraph(domNode)) {
                return <></>;
            }

            if (elementIsLink(domNode)) {
                const imgNode = findImgDescendant(domNode);
                const matchedAttachment =
                    imgNode &&
                    getAttachmentMatchingImgTag(imgNode, attachments);
                if (imgNode && matchedAttachment) {
                    // Linked embedded image (e.g. a logo or social-media icon in
                    // a signature). Only embedded (cid) attachments are inlined
                    // here; external/hotlinked images fall through to the link so
                    // we neither leak a request nor clutter the link with a long
                    // image URL.
                    const image = renderImgNode(imgNode, false);
                    const href = domNode.attribs?.href;
                    const linkText = getNodeText(domNode)?.trim();

                    if (!linkText) {
                        // Image-only link: the image itself is the link. Clicking
                        // confirms the destination before opening; no URL text is
                        // printed beside it. Unsafe/missing hrefs render the image
                        // alone.
                        if (href && URI_A_TAG_ALLOWED_LIST.test(href)) {
                            return (
                                <a
                                    href={href}
                                    onClick={onConfirmLinkClick(href)}
                                    className="inline-block cursor-pointer"
                                >
                                    {image}
                                </a>
                            );
                        }
                        return image;
                    }

                    // Image with visible link text: render the image beside the
                    // (confirm-on-click) text link. min-w-0 + wrap-break-word lets
                    // long text wrap instead of overflowing the nowrap flex row,
                    // while the icon keeps its size.
                    return (
                        <span className="inline-flex max-w-full items-center gap-2">
                            <span className="shrink-0">{image}</span>
                            <span className="min-w-0 wrap-break-word">
                                {renderLinkNode(domNode)}
                            </span>
                        </span>
                    );
                }
                return renderLinkNode(domNode);
            }

            if (elementIsImg(domNode)) {
                return renderImgNode(domNode);
            }

            return domNode;
        },
    });

    return { parsedContent, inlinedKeys };
};

const NBSP_PATTERN = /\u00A0/g;

// Tags that render something regardless of their (lack of) children, so a
// paragraph containing one is never "empty".
const CONTENTFUL_TAGS = new Set(['img', 'hr', 'video', 'audio', 'iframe']);

// A node contributes no visible content if it's whitespace/&nbsp; text, a <br>,
// or an inline wrapper (e.g. <span>, <b>) whose children are all blank. This is
// recursive so <p><span>&nbsp;</span></p> and nested wrappers count as empty.
const nodeIsBlank = (node: ChildNode): boolean => {
    if (node.type === 'tag') {
        if (node.name === 'br') return true;
        if (CONTENTFUL_TAGS.has(node.name)) return false;
        return (node.children ?? []).every(nodeIsBlank);
    }
    if (node.type === 'text') {
        return (
            (node as unknown as { data: string }).data
                .replace(NBSP_PATTERN, '')
                .trim() === ''
        );
    }
    return false;
};

const isEmptyParagraph = (element?: Element) => {
    if (element?.type !== 'tag' || element.name !== 'p') return false;
    return (element.children ?? []).every(nodeIsBlank);
};

const elementIsImg = (element?: Element) =>
    element?.type === 'tag' && element.name === 'img';

const elementIsLink = (element?: Element) =>
    element?.type === 'tag' && element.name === 'a';

// Finds the first <img> within an element's subtree (e.g. an <a> wrapping an
// icon). Returns undefined if the subtree contains no image.
const findImgDescendant = (element: Element): Element | undefined => {
    for (const child of element.children ?? []) {
        if (child.type !== 'tag') continue;
        if (child.name === 'img') return child;
        const nested = findImgDescendant(child);
        if (nested) return nested;
    }
    return undefined;
};

const getAttachmentMatchingImgTag = (
    element: Element,
    attachments?: List<TypedMap<TicketAttachment>>,
) => {
    if (!elementIsImg(element) || !attachments) {
        return null;
    }

    // Match img src with attachment contentId
    const src = stripCidPrefix(element.attribs?.src);

    return attachments.find(
        (attachment) => src && attachment.get('contentId') === src,
    );
};

const stripCidPrefix = (value?: string) =>
    value?.startsWith(URI.CONTENT_ID)
        ? value.slice(URI.CONTENT_ID.length)
        : value;

const DIGITS_ONLY = /^\d+$/;
const parseImgDimension = (value?: string) => {
    if (!value || !DIGITS_ONLY.test(value)) return undefined;
    return parseInt(value, 10) || undefined;
};

const URI_A_TAG_ALLOWED_LIST = /^(https:\/\/|mailto:|tel:)/i;

// Matches data:image/ URIs (e.g. data:image/png;base64,...) but excludes SVG for defense-in-depth
// (SVG can contain scripts — browsers sandbox SVG in <img>, but we err on the side of caution).
const DATA_IMAGE_ALLOWED = /^data:image\/(?!svg\+xml)[a-z]+[;,]/i;

const URI = Object.freeze({
    HTTPS: 'https://',
    CONTENT_ID: 'cid:',
    MAILTO: 'mailto:',
    TEL: 'tel:',
});
